import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Link as MuiLink,
} from '@mui/material';
import { Link } from 'react-router-dom';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#ff4081' },
    secondary: { main: '#00e5ff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif' },
});

function PrivacyPolicy() {
  const currentYear = new Date().getFullYear();
  const lastUpdated = new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Back Button */}
        <Box sx={{ position: 'fixed', top: 20, left: 20, zIndex: 1000 }}>
          <Button
            component={Link}
            to="/"
            size="small"
            variant="contained"
            sx={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: 'text.primary',
              '&:hover': { background: 'rgba(255, 255, 255, 0.2)' },
            }}
          >
            ← Back
          </Button>
        </Box>

        {/* Header */}
        <Container maxWidth="lg" sx={{ pt: 10, pb: 4 }}>
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(45deg, #ff4081 30%, #00e5ff 90%)',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              mb: 1,
              fontSize: { xs: '2.5rem', sm: '3.25rem', md: '4rem' },
            }}
          >
            Privacy Policy
          </Typography>
          <Typography align="center" sx={{ color: 'text.secondary' }}>
            Last updated: {lastUpdated}
          </Typography>
        </Container>

        {/* Content */}
        <Container maxWidth="md" sx={{ pb: 8 }}>
          <Paper
            sx={{
              p: { xs: 3, md: 4 },
              background: 'linear-gradient(135deg, rgba(255, 64, 129, 0.05) 0%, rgba(0, 229, 255, 0.05) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 3,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                ArtistPlay ("we," "our," or "us"), operated by Rowpe, values your privacy and is
                committed to protecting your personal information. This Privacy Policy explains how we
                collect, use, and share data when you use our website and related services.
              </Typography>

              <SectionTitle>1. Who We Are</SectionTitle>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                We provide an online platform where DJs can receive song suggestions and tips from their
                audience.
              </Typography>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600, mt: 1 }}>
                DJs:
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Create accounts to accept payments and manage requests.
              </Typography>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600, mt: 1 }}>
                Audience Members:
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Suggest songs and send tips without creating an account.
              </Typography>

              <SectionTitle>2. Information We Collect</SectionTitle>
              <SubSectionTitle>2.1. Information You Provide</SubSectionTitle>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                DJs (Account Holders):
              </Typography>
              <BulletedList items={[
                'Name, email address, password (encrypted), Stripe account details for payments.',
              ]} />
              <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Audience Members:
              </Typography>
              <BulletedList items={[
                'Email address (optional unless making a payment).',
                'Payment details are handled directly by Stripe (we do not store your full card details).',
                'Song suggestions text.',
              ]} />

              <SubSectionTitle>2.2. Information Collected Automatically</SubSectionTitle>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                When you use our platform, we may collect:
              </Typography>
              <BulletedList items={[
                'IP address, browser type, and device information.',
                'Usage data (pages visited, interaction with features).',
                'We use Firebase for hosting, authentication (for DJs), analytics, and database storage.',
              ]} />

              <SubSectionTitle>2.3. Payment Processing</SubSectionTitle>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                We use Stripe to process payments. Stripe may collect personal data such as:
              </Typography>
              <BulletedList items={[
                'Payment card information',
                'Billing details',
                'Transaction history',
              ]} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                You can view Stripe’s privacy policy here:{' '}
                <MuiLink href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
                  https://stripe.com/privacy
                </MuiLink>
              </Typography>

              <SectionTitle>3. How We Use Your Information</SectionTitle>
              <BulletedList items={[
                'Provide and operate the platform.',
                'Facilitate song suggestions and tip payments.',
                'Send payment receipts and related notifications.',
                'Improve our website and user experience.',
                'Comply with legal obligations.',
              ]} />

              <SectionTitle>4. How We Share Your Information</SectionTitle>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                We do not sell your personal information. We may share your information only with:
              </Typography>
              <BulletedList items={[
                'Stripe (for payment processing).',
                'Firebase (for hosting, authentication, and analytics).',
                'Legal authorities, if required by law.',
              ]} />

              <SectionTitle>5. Data Retention</SectionTitle>
              <BulletedList items={[
                'DJs: We retain account information until you delete your account.',
                'Audience Members: We keep email addresses linked to payments for as long as required by financial regulations.',
                'Song suggestions may be stored for service operation purposes.',
              ]} />

              <SectionTitle>6. Your Rights</SectionTitle>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Depending on your location, you may have the right to:
              </Typography>
              <BulletedList items={[
                'Access and receive a copy of your personal data.',
                'Correct inaccurate personal data.',
                'Request deletion of your personal data.',
                'Object to or restrict certain processing activities.',
              ]} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                You can contact us at <MuiLink href="mailto:admin@rowpe.com">admin@rowpe.com</MuiLink> to
                exercise these rights.
              </Typography>

              <SectionTitle>7. Security</SectionTitle>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                We use technical and organizational measures to protect your data, including:
              </Typography>
              <BulletedList items={[
                'Encrypted communication (HTTPS)',
                'Secure password storage (hashing)',
                'Restricted data access',
              ]} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                However, no system is 100% secure, and we cannot guarantee absolute security.
              </Typography>

              <SectionTitle>8. Children’s Privacy</SectionTitle>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Our services are not intended for children under 13. We do not knowingly collect data
                from children.
              </Typography>

              <SectionTitle>9. International Users</SectionTitle>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                If you access our platform from outside the country where our servers are located, your
                data may be transferred to and processed in a country with different data protection
                laws.
              </Typography>

              <SectionTitle>10. Changes to This Privacy Policy</SectionTitle>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                We may update this Privacy Policy from time to time. The latest version will always be
                posted on this page, with the effective date updated.
              </Typography>

              <SectionTitle>11. Contact Us</SectionTitle>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                If you have questions about this Privacy Policy, you can contact us at:
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Email: <MuiLink href="mailto:admin@rowpe.com">admin@rowpe.com</MuiLink>
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Address: Rowpe, 20-22 Wenlock Road, London, N1 7GU, United Kingdom
              </Typography>

              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 2 }}>
                © {currentYear} ArtistPlay. All rights reserved.
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="h5"
      sx={{
        color: 'primary.main',
        fontWeight: 700,
        mt: 2,
        fontSize: { xs: '1.25rem', sm: '1.4rem', md: '1.6rem' },
      }}
    >
      {children}
    </Typography>
  );
}

function SubSectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="h6"
      sx={{
        color: 'primary.main',
        fontWeight: 600,
        mt: 1.5,
        fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.25rem' },
      }}
    >
      {children}
    </Typography>
  );
}

function BulletedList({ items }: { items: string[] }) {
  return (
    <Box component="ul" sx={{ pl: 3, m: 0, color: 'text.secondary' }}>
      {items.map((text, idx) => (
        <Box key={idx} component="li" sx={{ mb: 1.25 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {text}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export default PrivacyPolicy;


